import { round } from 'lodash-es'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

export type PlanAxis = 'x' | 'y'

const X_AXIS = new ImmutableVector3(1, 0, 0)
const Y_AXIS = new ImmutableVector3(0, 1, 0)

const axisVector = (axis: PlanAxis) => (axis === 'x' ? X_AXIS : Y_AXIS)

const coordinate = (point: ImmutableVector3, axis: PlanAxis) => (axis === 'x' ? point.x : point.y)

/**
 * Base edge is p0–p1 and is always cartesian. Same y means the base runs along x.
 */
export const getBaseAxis = (points: ImmutableVector3[]): PlanAxis => {
  const [p0, p1] = points
  if (p0.y === p1.y) return 'x'
  return 'y'
}

const oppositeAxis = (axis: PlanAxis): PlanAxis => (axis === 'x' ? 'y' : 'x')

/**
 * Expansion is strict x or strict y, never the edge normal.
 * - edge 0 (base): orthogonal to the base axis
 * - edge 1: parallel to the base axis
 * - edge 2: orthogonal to the base axis (this edge may be diagonal)
 * - edge 3: parallel to the base axis
 */
export const getEdgeExpansionAxis = (points: ImmutableVector3[], edgeIndex: number): PlanAxis => {
  const baseAxis = getBaseAxis(points)
  return edgeIndex % 2 === 0 ? oppositeAxis(baseAxis) : baseAxis
}

const isDiagonal = (start: ImmutableVector3, end: ImmutableVector3) =>
  start.x !== end.x && start.y !== end.y

/**
 * Move one edge. Edges 0 and 2 translate strictly along their expansion axis.
 * On a trapezoid, edges 1 and 3 slide the corner they share with edge 2 along
 * edge 2, so that diagonal keeps its direction. The other corner stays on the base.
 */
export const moveVerticalSlabEdge = (
  points: ImmutableVector3[],
  edgeIndex: number,
  target: number,
  decimals: number,
): [ImmutableVector3, ImmutableVector3] => {
  const axis = getEdgeExpansionAxis(points, edgeIndex)
  const start = points[edgeIndex]
  const end = points[(edgeIndex + 1) % points.length]
  const edge2Preserved = (edgeIndex === 1 || edgeIndex === 3) && isDiagonal(points[2], points[3])

  if (!edge2Preserved) {
    return translateEdgeAlongAxis(
      start,
      end,
      axis,
      target,
      decimals,
      zDeltaForPlanarShift(points, start, end, axis, target, decimals),
    )
  }

  const moving = edgeIndex === 1 ? points[2] : points[3]
  const fixed = edgeIndex === 1 ? points[3] : points[2]
  const slid = slideAlongEdge(moving, fixed, axis, target, decimals)
  if (!slid) {
    return translateEdgeAlongAxis(
      start,
      end,
      axis,
      target,
      decimals,
      zDeltaForPlanarShift(points, start, end, axis, target, decimals),
    )
  }

  const basePoint = edgeIndex === 1 ? start : end
  const shiftedBase = new ImmutableVector3(
    axis === 'x' ? slid.x : basePoint.x,
    axis === 'y' ? slid.y : basePoint.y,
    basePoint.z,
  )

  return edgeIndex === 1 ? [shiftedBase, slid] : [slid, shiftedBase]
}

/**
 * Slide both endpoints by the same delta on `axis` so a diagonal edge keeps its slope.
 * Cartesian edges already share that coordinate, so both land on the cursor.
 * `zDelta` follows the roof plane so a sloped slab stays flat.
 */
export const translateEdgeAlongAxis = (
  start: ImmutableVector3,
  end: ImmutableVector3,
  axis: PlanAxis,
  target: number,
  decimals: number,
  zDelta = 0,
): [ImmutableVector3, ImmutableVector3] => {
  const anchor = (coordinate(start, axis) + coordinate(end, axis)) / 2
  const delta = round(target, decimals) - anchor
  const shift = axisVector(axis).multiplyScalar(delta).add(new ImmutableVector3(0, 0, zDelta))

  const move = (point: ImmutableVector3): ImmutableVector3 => {
    const moved = point.add(shift)
    return new ImmutableVector3(
      axis === 'x' ? round(moved.x, decimals) : point.x,
      axis === 'y' ? round(moved.y, decimals) : point.y,
      moved.z,
    )
  }

  return [move(start), move(end)]
}

/**
 * Height change that keeps both endpoints on the plane of p0, p1, p2.
 * A flat slab has slope 0, so its height stays put.
 */
const zDeltaForPlanarShift = (
  points: ImmutableVector3[],
  start: ImmutableVector3,
  end: ImmutableVector3,
  axis: PlanAxis,
  target: number,
  decimals: number,
): number => {
  const [p0, p1, p2] = points
  const normal = p1.sub(p0).cross(p2.sub(p0))
  if (Math.abs(normal.z) < 1e-9) return 0

  const slope = -(axis === 'x' ? normal.x : normal.y) / normal.z
  if (!Number.isFinite(slope)) return 0

  const anchor = (coordinate(start, axis) + coordinate(end, axis)) / 2
  const delta = round(target, decimals) - anchor
  return slope * delta
}

/** Place `moving` on the line through `moving` and `fixed` at `target` on `axis`. */
const slideAlongEdge = (
  moving: ImmutableVector3,
  fixed: ImmutableVector3,
  axis: PlanAxis,
  target: number,
  decimals: number,
): ImmutableVector3 | null => {
  const direction = fixed.sub(moving)
  const span = coordinate(direction, axis)
  if (span === 0) return null

  const snapped = round(target, decimals)
  const slid = moving.add(direction.multiplyScalar((snapped - coordinate(moving, axis)) / span))

  return new ImmutableVector3(
    axis === 'x' ? snapped : round(slid.x, decimals),
    axis === 'y' ? snapped : round(slid.y, decimals),
    slid.z,
  )
}
